/** Pure Trends logic. Weeks run Monday to Sunday and months are calendar months, both in Manila. */
export interface TrendMetric {
  id: string;
  label: string;
  value_type: 'count' | 'money' | 'rate';
  numerator_id: string | null;
  denominator_id: string | null;
  rate_format: 'percent' | 'money' | null;
}
/** Each day is [metric id, Manila entry date, value]. */
export interface TrendPayload {
  /** Today in Manila, from the database. */
  today: string;
  kind: 'daily' | 'snapshot';
  metrics: TrendMetric[];
  days: [string, string, number][];
}
export type TrendUnit = 'week' | 'month';
export interface TrendBucket {
  /** First day of the week or month, YYYY-MM-DD. */
  start: string;
  value: number | null;
  /** Days with a number (paired days for a rate). */
  days: number;
  /** Days that could have a number: all of them, or the elapsed ones for the current period. */
  of: number;
  /** Fewer days entered than possible, so the bar may be low. */
  partial: boolean;
  /** The period still in progress. Never compared. */
  current: boolean;
}

const DAY = 86_400_000;
const parse = (day: string) => Date.parse(`${day}T00:00:00Z`);
const format = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const addDays = (day: string, n: number) => format(parse(day) + n * DAY);

export function weekStart(day: string): string {
  const weekday = new Date(parse(day)).getUTCDay(); // 0 is Sunday
  return addDays(day, -((weekday + 6) % 7));
}
export function monthStart(day: string): string {
  return `${day.slice(0, 7)}-01`;
}
function addMonths(first: string, n: number): string {
  const d = new Date(parse(first));
  return format(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
}
function daysInMonth(first: string): number {
  return Math.round((parse(addMonths(first, 1)) - parse(first)) / DAY);
}
/** The earliest day the database must send: 12 weeks or 6 months, ending with the current one. */
export function trendSince(today: string, unit: TrendUnit): string {
  return unit === 'week' ? addDays(weekStart(today), -77) : addMonths(monthStart(today), -5);
}

export function trendBuckets(
  metric: TrendMetric,
  payload: TrendPayload,
  unit: TrendUnit,
): TrendBucket[] {
  const { today } = payload;
  const starts =
    unit === 'week'
      ? Array.from({ length: 12 }, (_, i) => addDays(weekStart(today), (i - 11) * 7))
      : Array.from({ length: 6 }, (_, i) => addMonths(monthStart(today), i - 5));
  const byMetric = new Map<string, Map<string, number>>();
  for (const [id, day, value] of payload.days) {
    const map = byMetric.get(id) ?? new Map<string, number>();
    map.set(day, Number(value));
    byMetric.set(id, map);
  }
  const own = byMetric.get(metric.id) ?? new Map<string, number>();
  const num = byMetric.get(metric.numerator_id ?? '') ?? new Map<string, number>();
  const den = byMetric.get(metric.denominator_id ?? '') ?? new Map<string, number>();
  return starts.map((start) => {
    const nextStart = unit === 'week' ? addDays(start, 7) : addMonths(start, 1);
    const current = today >= start && today < nextStart;
    const length = unit === 'week' ? 7 : daysInMonth(start);
    // Entry runs to yesterday, so the current period has this many days that could be in.
    const of = current ? Math.round((parse(today) - parse(start)) / DAY) : length;
    let sum = 0;
    let top = 0;
    let bottom = 0;
    let days = 0;
    for (let d = start; d < nextStart; d = addDays(d, 1)) {
      if (metric.value_type === 'rate') {
        const n = num.get(d);
        const m = den.get(d);
        if (n !== undefined && m !== undefined) {
          top += n;
          bottom += m;
          days += 1;
        }
      } else {
        const v = own.get(d);
        if (v !== undefined) {
          sum += v;
          days += 1;
        }
      }
    }
    const value =
      days === 0 ? null : metric.value_type === 'rate' ? (bottom === 0 ? null : top / bottom) : sum;
    return { start, value, days, of, partial: days < of, current };
  });
}

/** A trend needs two finished periods with numbers. The one in progress does not count. */
export function enoughHistory(buckets: TrendBucket[]): boolean {
  return buckets.filter((b) => !b.current && b.value !== null).length >= 2;
}
/** Last finished period against the one before it. The current period is never compared. */
export function lastFullChange(
  buckets: TrendBucket[],
): { last: number; previous: number | null; percent: number | null } | null {
  const done = buckets.filter((b) => !b.current);
  const last = done[done.length - 1]?.value ?? null;
  if (last === null) return null;
  const previous = done[done.length - 2]?.value ?? null;
  const percent = previous === null || previous === 0 ? null : ((last - previous) / previous) * 100;
  return { last, previous, percent };
}
